import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import Logo from "../../assets/logo.png";

import LegendTable from "../../components/Common/LegendTable";
import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";

import { fetchOccupancyInsights } from "../../store/thunks/analyticsThunks";
import { formatDateToISO, formatMonthToDateRange } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import showToast from "../../utils/toastNotifications";
import { load } from "@amcharts/amcharts4/.internal/core/utils/Net";

import { monthArray } from "../../constants/commonConstants";

const PortfolioPerformanceReport = () => {
  const dispatch = useDispatch();
  const { data, isLoading, error } = useSelector((state) => state.analytics);

  const allOccupancyData = Array.isArray(data?.propertyData)
    ? data.propertyData
    : [];
  const forecastedData = Array.isArray(data?.forecastedData)
    ? data.forecastedData
    : [];

  const [filters, setFilters] = useState({
    propertyId: "",
    month: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const rentChartRef = useRef(null);
  const rentChartTableRef = useRef(null);
  const forecastChartRef = useRef(null);
  const forecastChartTableRef = useRef(null);

  const uniquePropertyOptions = useMemo(() => {
    if (!allOccupancyData.length) {
      return [
        {
          label: "🚫 No property",
          value: "none",
          disabled: true,
        },
      ];
    }
  
    return allOccupancyData.map((item) => ({
      label: item.propertyName,
      value: item._id,
    }));
  }, [allOccupancyData]);

  useEffect(() => {
    const propertyName = searchParams.get("propertyName") || "";
    const month = searchParams.get("month") || "";

    const propertyId = allOccupancyData.find(
      (property) => property.propertyName === propertyName
    )?._id;

    setFilters({
      propertyId: propertyId || "",
      month: month || "",
    });
  }, [searchParams]);

  const fetchData = useCallback(() => {
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);

    const query = {
      propertyId: filters.propertyId,
      month: filters.month,
    };
    dispatch(fetchOccupancyInsights(query)).finally(() => {
      setIsRequestInProgress(false);
    });
  }, [filters, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const queryParams = new URLSearchParams();


    const selectedProperty = allOccupancyData.find(
      (property) => property._id === filters.propertyId
    );


    queryParams.set(
      "propertyName",
      selectedProperty ? selectedProperty.propertyName : ""
    );
    queryParams.set("month", filters.month || "");
    setSearchParams(queryParams);
  };

  const selectedProperty = allOccupancyData.find(
    (property) => property._id === filters.propertyId
  );
  const selectedPropertyName = selectedProperty
    ? selectedProperty.propertyName
    : "All properties";

  const filteredData = useMemo(() => {
    return allOccupancyData.filter((item) => {
      const matchProperty =
        !filters.propertyId || item._id === filters.propertyId;
      return matchProperty;
    });
  }, [allOccupancyData, filters.propertyId]);

  const filteredForecastedData = useMemo(
    () =>
       {
    // return filters.propertyId
    //     ? forecastedData.filter(
    //         (item) => item.propertyId === filters.propertyId
    //       )
    //     : forecastedData;
    return forecastedData;
  }, 
    [forecastedData, filters.propertyId]
  );

  const aggregated = useMemo(() => {
    return filteredData.reduce(
      (acc, curr) => {
        acc.rentCollected += parseFloat(curr.rentCollect || "0");
        acc.rentExpected += parseFloat(curr.rentExpected || "0");
        acc.totalUnits += parseInt(curr.numberOfUnits || "0");
        return acc;
      },
      { rentCollected: 0, rentExpected: 0, totalUnits: 0 }
    );
  }, [filteredData]);

  const rentExpected = aggregated.rentExpected === 0 ? 1 : aggregated.rentExpected;
  const rentCollectedPercentage = rentExpected > 0 ? (aggregated.rentCollected / rentExpected) * 100 : 0;
  const rentExpectedPercentage = rentExpected > 0 ? (rentExpected / rentExpected) * 100 : 0;

  const rentChartSeries = useMemo(
    () => [
      {
        name: "Tenant Payment Status",
        data: [rentCollectedPercentage, rentExpectedPercentage],
      },
    ],
    [rentCollectedPercentage, rentExpectedPercentage]
  );

  const rentChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: 400,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          horizontal: false,
          columnWidth: "40%",
          distributed: true, 
        },
      },
      dataLabels: {
        enabled: false,
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          colors: ["#000"],
        },
        formatter: (val) => `${val}%`,
      },
      xaxis: {
        categories: ["Collected amount", "Expected amount"],
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#00000099", "#00000099"],
          },
        },
      },
      yaxis: {
        title: {
          text: "",
        },
        min: 0,
        max: rentCollectedPercentage > 100 ? rentCollectedPercentage : 100,
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#00000099"],
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}%`,
        },
      },
      colors: ["#6E90F4", "#A068E8"],
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          gradientToColors: ["rgba(110, 144, 244, 0.25)", "rgba(146, 80, 229, 0.25)"],
          stops: [0, 100],
          opacityFrom: 1,
          opacityTo: 1,
        },
      },
    }),
    [rentCollectedPercentage, rentExpectedPercentage]
  );

  const legendData = useMemo(
    () => [
      {
        label: "Expected",
        value: aggregated.rentExpected.toFixed(2),
        color: "#A068E8",
        percent: rentExpectedPercentage,
      },
      {
        label: "Collected",
        value: aggregated.rentCollected.toFixed(2),
        color: "#6E90F4",
        percent: rentCollectedPercentage,
      },
    ],
    [aggregated, rentExpectedPercentage, rentCollectedPercentage]
  );

  const maxForecastValue = useMemo(
    () =>
      Math.max(
        ...filteredForecastedData.map((item) => parseFloat(item.value)),
        1
      ),
    [filteredForecastedData]
  );

  const forecastedPercentageData = useMemo(
    () =>
      filteredForecastedData.map((item) => ({
        label: item.label,
        value: (parseFloat(item.value) / maxForecastValue) * 100,
      })),
    [filteredForecastedData, maxForecastValue]
  );

  const forecastedChartOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 400,
        toolbar: { show: false },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          gradientToColors: ["rgba(146, 80, 229, 0.25)"],
          stops: [0, 100],
          opacityFrom: 1,
          opacityTo: 1,
        },
      },
      xaxis: {
        categories: filteredForecastedData.map((item) => item.label),
        title: {
          text: "Months",
          style: { fontSize: "14px", fontWeight: 600 },
        },
        labels: {
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
          formatter: (val) => (/^(Pre|Post)-/.test(val) ? "" : val),
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        },
        title: {
          text: "Income",
          style: { fontSize: "14px", fontWeight: 600 },
        },
      },
      tooltip: {
        y: { formatter: (val) => `${val}%` },
      },
      colors: ["#9250E5"],
    }),
    [filteredForecastedData]
  );

  const forecastedChartSeries = useMemo(
    () => [
      {
        name: "Income",
        data: forecastedPercentageData.map((item) =>
          parseFloat(item.value.toFixed(2))
        ),
      },
    ],
    [forecastedPercentageData]
  );

  const createPortfolioInfoCardSVG = (data) => {
    let propertyXPosition = 270;
    let propertyYPosition = 70;
    if (data.propertyCount > 1000 || data.totalUnits > 1000) {
      propertyXPosition = 25;
      propertyYPosition = 90;
    }

    const svgContent = `
      <svg width="800" height="200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="2" flood-color="rgba(45, 63, 115, 0.15)"/>
          </filter>
          <filter id="containerShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="rgba(0,0,0,0.06)"/>
          </filter>
          <!-- Fixed background color -->
          <pattern id="cardBg" patternUnits="userSpaceOnUse" width="1" height="1">
            <rect width="1" height="1" fill="#9FB6FB" opacity="0.1"/>
          </pattern>
        </defs>
        
        <!-- Main Container Background -->
        <rect x="5" y="5" width="790" height="120" rx="10" fill="white" filter="url(#containerShadow)"/>
        
        <!-- Left Side - Title and Summary -->
        <text x="25" y="70" font-family="Arial, sans-serif" font-size="18" font-weight="600" fill="#4772F1">
          Total Properties and Units
        </text>
        
        <text x="${propertyXPosition}" y="${propertyYPosition}" font-family="Arial, sans-serif" font-size="14" font-weight="500" fill="#071952">
          ${data.propertyCount} Properties / ${data.totalUnits} Units
        </text>
        
        <!-- Right Side - InfoCards Container -->
        <g transform="translate(450, 20)">
          <!-- Properties Card -->
          <rect x="0" y="0" width="140" height="80" rx="5" fill="url(#cardBg)" filter="url(#cardShadow)" stroke="#f0f0f0" stroke-width="1"/>
          <text x="70" y="30" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Properties
          </text>
          <text x="70" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${data.propertyCount}
          </text>
          
          <!-- Units Card -->
          <rect x="160" y="0" width="140" height="80" rx="5" fill="url(#cardBg)" filter="url(#cardShadow)" stroke="#f0f0f0" stroke-width="1"/>
          <text x="230" y="30" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Units
          </text>
          <text x="230" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${data.totalUnits}
          </text>
        </g>
        
        <!-- Divider Line -->
        <line x1="440" y1="30" x2="440" y2="170" stroke="#f0f0f0" stroke-width="1" opacity="0.5"/>
      </svg>
    `;
    
    return svgContent;
  };

  const downloadPdf = async () => {
    try {

      let month;
      if(!filters.month || filters.month === '') {
        month = new Date().toLocaleString('default', { month: 'long' });
      } else {
        month = filters.month;
      }
      const {startDate, endDate} = formatMonthToDateRange(month);

      let dateRangeText = `${startDate} to ${endDate}`;
      let propertyName = selectedPropertyName;

      if(filteredData.length === 0) {
        showToast("info", "No data available for the selected filters.");
        setIsGeneratingPdf(false);
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');

      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;

      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

      yPosition += 20;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Portfolio Performance Report', 20, yPosition);

      yPosition += 10;

      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);

      pdf.setTextColor(0, 0, 0);
      pdf.text(dateRangeText, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Property:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(propertyName, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;

      // Applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      if (propertyName) {
        pdf.text(`• Property Filter: ${propertyName}`, 25, yPosition);
        yPosition += 5;
      }
      
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 10;

      // Create SVG InfoCard with gradients
      const portfolioInfoCardContent = createPortfolioInfoCardSVG({
        propertyCount:uniquePropertyOptions.length,
        totalUnits: aggregated.totalUnits
      });
      
      const portfolioInfoCardCanvas = await svgToCanvas(portfolioInfoCardContent, 800, 200);
      const portfolioInfoCardImgData = portfolioInfoCardCanvas.toDataURL('image/png');
      const portfolioInfoCardWidth = 170; // Full width within margins
      const portfolioInfoCardHeight = (200 * portfolioInfoCardWidth) / 800; // Proportional height

      pdf.addImage(portfolioInfoCardImgData, 'PNG', 20, yPosition, portfolioInfoCardWidth, portfolioInfoCardHeight);
      yPosition += portfolioInfoCardHeight;

      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Rent Collected vs. Expected', 20, yPosition);
      yPosition += 10;

      let rentChartHeight = 0;
      if (rentChartRef.current) {
        const canvas = await html2canvas(rentChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        rentChartHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, rentChartHeight);
      }

      if (rentChartTableRef.current){
        const legendCanvas = await html2canvas(rentChartTableRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition + 20, legendWidth, legendHeight);
      }

      yPosition += rentChartHeight + 10;

      pdf.addPage();
      yPosition = 25;

      //Forecast Chart Section
      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Forecasted Income', 20, yPosition);
      yPosition += 10;

      if (forecastChartRef.current) {
        const canvas = await html2canvas(forecastChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        const forecastChartHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, forecastChartHeight);
      }

      if (forecastChartTableRef.current){
        const legendCanvas = await html2canvas(forecastChartTableRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition, legendWidth, legendHeight);
      }

      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        const footerY = pageHeight - 15;

        // header section
        pdf.setFontSize(10);
        pdf.setTextColor(71, 114, 241); // Blue color for brand
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', xPosition, 10);
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('Real Estate Management Software', xPosition, 14);
        
        pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);

        // Footer section
        pdf.setFontSize(8);
        pdf.setTextColor(71, 114, 241);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, footerY + 3);
        
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('|', 42, footerY + 3);
        pdf.text('www.tenafix.co', 44, footerY + 3);
        pdf.text('|', 64, footerY + 3);
        pdf.text('support@tenafix.co', 66, footerY + 3);
        
        // System generated note
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('This is a system-generated report.', 20, footerY + 6);
        pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
        
        // Page number (right aligned)
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }

      // Save PDF
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      pdf.save(`portfolio-performance-report-${currentDate}.pdf`);

    } catch (error) {
      showToast("error", "Failed to generate PDF. Please try again.");
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }

  }

  const handleDownload = () => {
    setIsGeneratingPdf(true);
    setTimeout(() => downloadPdf(), 0);
  }

  const propertiesCount = filteredData.length;


  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-[30px]">
      <TableHeader
        searchValue={filters.propertyId}
        onSearchSubmit={handleSearch}
        filters={[
          {
            name: "Filter by Property",
            value: filters.selectedPropertyName,
            onChange: (val) =>
              setFilters((prev) => ({ ...prev, propertyId: val })),
            options: [
              ...(uniquePropertyOptions.length > 0 &&
              !uniquePropertyOptions[0].disabled
                ? [{ label: "All properties", value: "" }]
                : []),
              ...uniquePropertyOptions,
            ],
          },
          {
            name: "Filter by Month",
            value: filters.month,
            onChange: (val) =>
              setFilters((prev) => ({ ...prev, month: val })),
            options: monthArray.map((month) => ({
              label: month,
              value: month,
            })),
          }
        ]}
        buttons={[
          {
            label: isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "SEARCH"
            ),
            onClick: handleSearch,
            disabled: isLoading,
            variant: "main",
            className:
              "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[100px] uppercase font-medium flex items-center justify-center",
          },
          {
            label: "DOWNLOAD REPORT",
            onClick: handleDownload,
            loading: isGeneratingPdf,
            disabled: isGeneratingPdf || isLoading,
            className:
              "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[170px] uppercase font-bold",
          },
        ]}
        datePickerClass="sm:w-full md:w-full lg:w-full flex flex-grow w-full"
        buttonWrapperClass="sm:mr-0 mx-auto w-full flex flex-row [@media(min-width:360px)]:w-full   [@media(min-width:360px)]:flex [@media(min-width:360px)]:flex-row justify-center items-center [@media(min-width:640px)]:justify-start [@media(min-width:768px)]:justify-end lg:justify-end"
        containerClass="md:grid lg:grid-cols-3 md:grid-cols-3 flex flex-wrap lg:flex-nowrap xl:grid xl:grid-rows-1"  //[@media(min-width:1516px)]:
        dropdownWrapperClass="sm:max-w-full md:max-w-full flex flex-grow w-full"
      />

      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <div className="flex gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[10px] gap-y-[30px] $  flex-col sm:flex-row lg:flex-row sm:justify-between ">
          <div className="text-[14px] sm:text-[16px] md:text-[18px] xl:text-[22px] text-[#4772F1] font-[600px] font-inter flex items-center justify-center sm:justify-start lg:justify-start gap-5">
            Total Properties & Units
            <span className="text-[12px] sm:text-[14px] md:text-[16px] xl:text-[18px] font-[500] font-inter text-[#071952] flex items-center ">
              {/*{filters.propertyId ? 1 : uniquePropertyOptions.length} Properties*/}
              {filters.propertyId ? 1 : propertiesCount} Properties
              / {aggregated.totalUnits} Units
            </span>{" "}
          </div>

          <div className="flex gap-[15px] ">
            <div
              className="w-full rounded-[5px]"
              style={{ boxShadow: "0px 4px 4px 0px rgba(45, 63, 115, 0.15)" }}
            >
              <InfoCard
                label="Properties"
               // value={filters.propertyId ? 1 : uniquePropertyOptions.length}
                value={filters.propertyId ? 1 : propertiesCount}
                className="max-h-[100px] mx-auto bg-[#9FB6FB1A] w-full sm:w-[130px] "
                labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] flex justify-center leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                valueStyle="font-inter font-semibold text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] flex justify-center leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                popoverTextStyle="text-level-4"
              />
            </div>
            <div
              className="w-full rounded-[5px]"
              style={{ boxShadow: "0px 4px 4px 0px rgba(45, 63, 115, 0.15)" }}
            >
              <InfoCard
                label="Units"
                value={aggregated.totalUnits}
                className="max-h-[100px] mx-auto bg-[#9FB6FB1A] w-full sm:w-[130px]"
                labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] flex justify-center lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                valueStyle="font-inter font-semibold text-[18px] sm:text-[20px] md:text-[22px] flex justify-center lg:text-[24px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                popoverTextStyle="text-level-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Rent Collected vs Expected */}
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4 xl:gap-[60px] mt-[30px]">
        <div>
          <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
            Rent Collected vs. Expected
          </h2>
          <div
           ref={rentChartRef}
           className="rounded-[5px] xl:w-full border-[2px] border-[#F9F9F9] xl:p-[30px]   shadow-box-9696"
          >
            <ReactApexChart
              options={rentChartOptions}
              series={rentChartSeries}
              type="bar"
              height={400}
            />
          </div>
        </div>

        <div 
          ref={rentChartTableRef}
          className="h-[158px] md:relative top-32  rounded-[5px] xl:w-full border-[2px] border-[#F9F9F9] xl:p-[30px] p-2  shadow-box-9696"
        >
          <LegendTable
            data={legendData}
            showDot={true}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
        </div>
      </div>

      {/* Forecasted Income */}
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
          Forecasted Income
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4 xl:gap-[60px]">
          <div
            ref={forecastChartRef}
            className="rounded-[5px] xl:w-full border-[2px] border-[#F9F9F9] xl:p-[30px]   shadow-box-9696"
          >
            <ReactApexChart
              options={forecastedChartOptions}
              series={forecastedChartSeries}
              type="area"
              height={400}
            />
          </div>
          <div
          ref={forecastChartTableRef}
            className="rounded-[5px] xl:w-full border-[2px] border-[#F9F9F9] p-2 xl:p-[30px]   shadow-box-9696"
          >
            <LegendTable
              data={forecastedPercentageData
                .filter((item) => !/^(Pre|Post)-/.test(item.label))
                .map((item) => ({
                  label: item.label,
                  value: parseFloat(
                    (item.value * maxForecastValue) / 100
                  ).toFixed(2),
                  percent: parseFloat(item.value.toFixed(2)),
                }))}
              showDot={false}
              className="mt-4"
              classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
              classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
              classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPerformanceReport;
